import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance } from '@/api/msalInstance';
import { graphScopes } from '@/config/auth';

interface GraphUser {
  displayName: string;
  mail: string;
  jobTitle: string | null;
}

interface GraphResponse {
  value: Array<{
    displayName: string;
    mail: string | null;
    jobTitle: string | null;
  }>;
}

// Cache the Graph token to avoid repeated acquireTokenSilent iframe calls.
// MSAL's acquireTokenSilent opens a hidden iframe to Azure AD every time if it
// doesn't have a cached token for Graph scopes. We cache it ourselves after the
// first acquisition and reuse until 5 minutes before expiry.
let cachedGraphToken: string | null = null;
let cachedGraphTokenExpiry = 0;
let inflightTokenRequest: Promise<string> | null = null;

async function getGraphToken(): Promise<string> {
  // Return cached token if still valid (with 5-minute buffer)
  if (cachedGraphToken && Date.now() < cachedGraphTokenExpiry) {
    return cachedGraphToken;
  }

  // Deduplicate concurrent requests
  if (inflightTokenRequest) return inflightTokenRequest;

  inflightTokenRequest = acquireGraphToken().finally(() => {
    inflightTokenRequest = null;
  });
  return inflightTokenRequest;
}

async function acquireGraphToken(): Promise<string> {
  const msal = getMsalInstance();
  if (!msal) throw new Error('MSAL not initialized');

  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) throw new Error('No authenticated user');

  try {
    const response = await msal.acquireTokenSilent({
      scopes: graphScopes,
      account: accounts[0],
    });
    cachedGraphToken = response.accessToken;
    // Cache until 5 minutes before expiry
    cachedGraphTokenExpiry = response.expiresOn
      ? response.expiresOn.getTime() - 5 * 60 * 1000
      : Date.now() + 50 * 60 * 1000; // fallback: 50 minutes
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({
        scopes: graphScopes,
        account: accounts[0],
      });
    }
    throw error;
  }
}

export interface GraphProfile {
  jobTitle: string | null;
  officeLocation: string | null;
}

export async function getMyProfile(): Promise<GraphProfile> {
  const token = await getGraphToken();
  const res = await fetch(
    'https://graph.microsoft.com/v1.0/me?$select=jobTitle,officeLocation',
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Graph API error: ${res.status}`);
  return res.json() as Promise<GraphProfile>;
}

export async function searchGraphUsers(query: string): Promise<GraphUser[]> {
  if (query.length < 2) return [];

  const token = await getGraphToken();
  const escaped = query.replace(/'/g, "''");
  const filter = `startswith(displayName,'${escaped}') or startswith(mail,'${escaped}')`;
  const params = new URLSearchParams({
    $filter: filter,
    $select: 'displayName,mail,jobTitle',
    $top: '10',
  });

  const res = await fetch(
    `https://graph.microsoft.com/v1.0/users?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!res.ok) throw new Error(`Graph API error: ${res.status}`);

  const data = (await res.json()) as GraphResponse;
  return data.value
    .filter((u) => u.mail)
    .map((u) => ({
      displayName: u.displayName,
      mail: u.mail!,
      jobTitle: u.jobTitle,
    }));
}
